// ==========================================================
// Target Blueprint Zoom & Inspection Modal Controller
// Provides interactive high-resolution artwork inspection,
// multi-level zoom, pan navigation, grid overlay, & color swatches.
// ==========================================================

import type { StageInfo } from '../stages/StageData';
import { getLocalizedStage } from '../stages/StageData';
import { i18n } from '../i18n/I18nService';
import { sounds } from '../audio/SoundEffects';

export class TargetZoomModalController {
  private modalEl: HTMLElement;
  private canvasEl: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private stageNameEl: HTMLElement;
  private zoomLevelTextEl: HTMLElement;
  private colorsChipsEl: HTMLElement;
  private gridOverlayEl: HTMLElement;
  private viewportWrapEl: HTMLElement;
  private canvasContainerEl: HTMLElement;
  private titleBadgeEl: HTMLElement;
  private hintTextEl: HTMLElement;
  private paletteLabelEl: HTMLElement;

  private btnZoomIn: HTMLButtonElement;
  private btnZoomOut: HTMLButtonElement;
  private btnZoomReset: HTMLButtonElement;
  private btnToggleGrid: HTMLButtonElement;
  private btnClose: HTMLButtonElement;

  private currentStage: StageInfo | null = null;
  private zoomLevel: number = 1.0; // 1.0 (100%) to 3.0 (300%)
  private panX: number = 0;
  private panY: number = 0;
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private isGridVisible: boolean = false;

  // Touch gesture support
  private activeTouches: Map<number, { x: number; y: number }> = new Map();
  private initialTouchDistance: number | null = null;
  private initialTouchZoom: number = 1.0;

  constructor() {
    this.modalEl = document.getElementById('modal-target-zoom')!;
    this.canvasEl = document.getElementById('target-zoom-hd-canvas') as HTMLCanvasElement;
    this.ctx = this.canvasEl.getContext('2d')!;
    this.stageNameEl = document.getElementById('target-zoom-stage-name')!;
    this.zoomLevelTextEl = document.getElementById('target-zoom-level-text')!;
    this.colorsChipsEl = document.getElementById('target-zoom-colors-chips')!;
    this.gridOverlayEl = document.getElementById('target-zoom-grid-overlay')!;
    this.viewportWrapEl = document.getElementById('target-zoom-viewport-wrap')!;
    this.canvasContainerEl = document.getElementById('target-zoom-canvas-container')!;
    this.titleBadgeEl = document.getElementById('target-zoom-title-badge')!;
    this.hintTextEl = document.getElementById('target-zoom-hint-text')!;
    this.paletteLabelEl = document.getElementById('target-zoom-palette-label')!;

    this.btnZoomIn = document.getElementById('btn-zoom-in-blueprint') as HTMLButtonElement;
    this.btnZoomOut = document.getElementById('btn-zoom-out-blueprint') as HTMLButtonElement;
    this.btnZoomReset = document.getElementById('btn-zoom-reset-blueprint') as HTMLButtonElement;
    this.btnToggleGrid = document.getElementById('btn-zoom-toggle-grid') as HTMLButtonElement;
    this.btnClose = document.getElementById('btn-close-target-zoom') as HTMLButtonElement;

    this.initEvents();
    this.updateLanguageUI();

    i18n.subscribe(() => {
      this.updateLanguageUI();
      if (this.currentStage && this.isOpen()) {
        this.renderStage(this.currentStage);
      }
    });
  }

  private initEvents() {
    // Close actions
    this.btnClose?.addEventListener('click', () => {
      sounds.playClick();
      this.close();
    });

    this.modalEl?.addEventListener('click', (e) => {
      if (e.target === this.modalEl) {
        sounds.playClick();
        this.close();
      }
    });

    // Zoom controls
    this.btnZoomIn?.addEventListener('click', () => {
      sounds.playClick();
      this.setZoom(this.zoomLevel + 0.35);
    });

    this.btnZoomOut?.addEventListener('click', () => {
      sounds.playClick();
      this.setZoom(this.zoomLevel - 0.35);
    });

    this.btnZoomReset?.addEventListener('click', () => {
      sounds.playClick();
      this.resetView();
    });

    // Grid toggle
    this.btnToggleGrid?.addEventListener('click', () => {
      sounds.playClick();
      this.toggleGrid();
    });

    // Viewport mouse / pointer drag
    this.viewportWrapEl?.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      this.viewportWrapEl.setPointerCapture(e.pointerId);
      this.activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.activeTouches.size === 1) {
        this.isDragging = true;
        this.dragStartX = e.clientX - this.panX;
        this.dragStartY = e.clientY - this.panY;
        this.viewportWrapEl.style.cursor = 'grabbing';
      } else if (this.activeTouches.size === 2) {
        // Start 2-finger pinch
        this.isDragging = false;
        const pts = Array.from(this.activeTouches.values());
        this.initialTouchDistance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        this.initialTouchZoom = this.zoomLevel;
      }
    });

    this.viewportWrapEl?.addEventListener('pointermove', (e) => {
      if (!this.activeTouches.has(e.pointerId)) return;
      this.activeTouches.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.activeTouches.size === 1 && this.isDragging) {
        this.panX = e.clientX - this.dragStartX;
        this.panY = e.clientY - this.dragStartY;
        this.clampPan();
        this.applyTransform();
      } else if (this.activeTouches.size === 2 && this.initialTouchDistance !== null) {
        const pts = Array.from(this.activeTouches.values());
        const currentDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const scaleFactor = currentDist / this.initialTouchDistance;
        this.setZoom(this.initialTouchZoom * scaleFactor);
      }
    });

    const endPointer = (e: PointerEvent) => {
      if (this.viewportWrapEl.hasPointerCapture(e.pointerId)) {
        this.viewportWrapEl.releasePointerCapture(e.pointerId);
      }
      this.activeTouches.delete(e.pointerId);
      if (this.activeTouches.size === 0) {
        this.isDragging = false;
        this.initialTouchDistance = null;
        this.viewportWrapEl.style.cursor = 'grab';
      } else if (this.activeTouches.size === 1) {
        const remaining = Array.from(this.activeTouches.values())[0];
        this.isDragging = true;
        this.dragStartX = remaining.x - this.panX;
        this.dragStartY = remaining.y - this.panY;
      }
    };

    this.viewportWrapEl?.addEventListener('pointerup', endPointer);
    this.viewportWrapEl?.addEventListener('pointercancel', endPointer);

    // Mouse wheel zoom
    this.viewportWrapEl?.addEventListener('wheel', (e) => {
      e.preventDefault();
      const zoomDelta = e.deltaY < 0 ? 0.2 : -0.2;
      this.setZoom(this.zoomLevel + zoomDelta);
    }, { passive: false });

    // Global shortcut listener: ESC or Space or Z to toggle / close
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        e.preventDefault();
        this.close();
      } else if ((e.key === ' ' || e.key === 'z' || e.key === 'Z') && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        // Toggle only when on game screen or blueprint is open
        const titleModal = document.getElementById('modal-title-menu');
        const isTitleVisible = titleModal && !titleModal.classList.contains('hidden');
        if (!isTitleVisible && this.currentStage) {
          e.preventDefault();
          if (this.isOpen()) {
            this.close();
          } else {
            this.open(this.currentStage);
          }
        }
      }
    });
  }

  public isOpen(): boolean {
    return !this.modalEl.classList.contains('hidden');
  }

  public open(stage: StageInfo) {
    this.currentStage = stage;
    this.modalEl.classList.remove('hidden');
    sounds.playTapePlace();
    this.resetView();
    this.renderStage(stage);
  }

  public close() {
    this.modalEl.classList.add('hidden');
  }

  public setZoom(targetLevel: number) {
    this.zoomLevel = Math.min(3.0, Math.max(1.0, Math.round(targetLevel * 100) / 100));
    this.zoomLevelTextEl.textContent = `${Math.round(this.zoomLevel * 100)}%`;
    this.clampPan();
    this.applyTransform();
  }

  public resetView() {
    this.zoomLevel = 1.0;
    this.panX = 0;
    this.panY = 0;
    this.zoomLevelTextEl.textContent = '100%';
    this.applyTransform();
  }

  private clampPan() {
    // Dynamic boundary clamping based on zoom
    const maxPan = 280 * (this.zoomLevel - 1);
    this.panX = Math.min(maxPan, Math.max(-maxPan, this.panX));
    this.panY = Math.min(maxPan, Math.max(-maxPan, this.panY));
  }

  private applyTransform() {
    this.canvasContainerEl.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`;
  }

  public toggleGrid() {
    this.isGridVisible = !this.isGridVisible;
    if (this.isGridVisible) {
      this.gridOverlayEl.classList.remove('hidden');
      this.btnToggleGrid.classList.add('active');
    } else {
      this.gridOverlayEl.classList.add('hidden');
      this.btnToggleGrid.classList.remove('active');
    }
  }

  private renderStage(stage: StageInfo) {
    const localized = getLocalizedStage(stage, i18n.getLanguage());
    this.stageNameEl.textContent = `STAGE ${stage.id}: ${localized.name} (${localized.category})`;

    // High-resolution canvas rendering (600x600 px internal buffer)
    const cw = this.canvasEl.width;
    const ch = this.canvasEl.height;
    this.ctx.clearRect(0, 0, cw, ch);

    this.ctx.save();
    // High quality scaling with margin padding
    const scale = Math.min(cw / 840, ch / 920) * 0.94;
    const ox = (cw - 840 * scale) / 2;
    const oy = (ch - 920 * scale) / 2;
    this.ctx.translate(ox, oy);
    this.ctx.scale(scale, scale);

    // Draw full shaded artwork
    stage.drawObject(this.ctx, 840, 920, 'base');
    stage.drawObject(this.ctx, 840, 920, 'targetPreview');
    stage.drawObject(this.ctx, 840, 920, 'outline');
    this.ctx.restore();

    // Populate Target Colors Chips
    this.colorsChipsEl.innerHTML = '';
    localized.targetColors.forEach(tc => {
      const chip = document.createElement('div');
      chip.className = 'blueprint-color-chip';
      chip.innerHTML = `
        <span class="chip-swatch" style="background: ${tc.hex};"></span>
        <span class="chip-hex">${tc.hex}</span>
        <span class="chip-name">${tc.name}</span>
      `;
      this.colorsChipsEl.appendChild(chip);
    });
  }

  public updateLanguageUI() {
    const t = i18n.dict.blueprint;
    if (this.titleBadgeEl) this.titleBadgeEl.textContent = t.titleBadge;
    if (this.hintTextEl) this.hintTextEl.textContent = t.hintText;
    if (this.paletteLabelEl) this.paletteLabelEl.textContent = t.requiredColors;
    if (this.btnToggleGrid) this.btnToggleGrid.title = t.gridBtn;
    if (this.btnZoomIn) this.btnZoomIn.title = t.zoomIn;
    if (this.btnZoomOut) this.btnZoomOut.title = t.zoomOut;
    if (this.btnZoomReset) this.btnZoomReset.title = t.reset;
    if (this.btnClose) this.btnClose.title = t.closeBtn;
  }
}
